// DrawPolygonTool — click-to-add-points polygon drawing
// Click adds vertex, double-click or click near first vertex closes, ESC cancels, Backspace removes last

import * as THREE from 'three'
import type { Tool, ToolContext } from '@/types/editor'
import type { ToolManager } from '../ToolManager'
import type { DimensionOverlay } from '@/ui/DimensionOverlay'
import { DPM } from '@/constants/coordinates'
import { calculatePolygonAreaFromPoints } from '@/utils/areaCalculation'
import { getViewer, getToolMode } from '@/ui/state'
import { debug } from '@/utils/debug'

export interface DrawPolygonConfig {
  /** Called when drawing finishes. Returns new shape ID. */
  onCreate: (polygon: {
    x: number; y: number
    width: number; height: number
    points: Array<{ x: number; y: number }>
  }) => string
  /** Preview color (default: 0x4ecca3) */
  previewColor?: number
  /** Minimum number of points to create (default: 3) */
  minPoints?: number
  /** Optional dimension overlay for live measurements */
  overlay?: DimensionOverlay
}

/** Distance in pixels to snap to first vertex and close the polygon */
const CLOSE_THRESHOLD_PX = 15

export class DrawPolygonTool implements Tool {
  readonly name = 'draw-polygon'
  readonly cursor = 'crosshair'
  readonly hint = 'tool.hint.drawPolygon'

  private toolManager: ToolManager
  private config: DrawPolygonConfig
  private minPoints: number

  // Vertices in editor coords (dots, absolute)
  private vertices: Array<{ x: number; y: number }> = []
  // Current mouse position in world coords (for rubberband)
  private currentWorld: THREE.Vector3 | null = null

  // Screen positions for close-threshold detection
  private firstVertexScreen: { x: number; y: number } | null = null
  // Camera reference for overlay projection
  private lastCamera: THREE.Camera | null = null

  // Preview
  private previewGroup: THREE.Group
  private previewFill: THREE.Mesh | null = null
  private previewLine: THREE.Line | null = null

  constructor(toolManager: ToolManager, scene: THREE.Scene, config: DrawPolygonConfig) {
    this.toolManager = toolManager
    this.config = config
    this.minPoints = config.minPoints ?? 3

    this.previewGroup = new THREE.Group()
    this.previewGroup.name = 'draw-polygon-preview'
    scene.add(this.previewGroup)
  }

  activate(): void {
    this.vertices = []
    this.currentWorld = null
    this.firstVertexScreen = null
    const selMgr = getViewer()?.getScene()?.getSelectionManager()
    selMgr?.deselect()
    selMgr?.clearHover()
    selMgr?.setEnabled(false)
    debug.log('DrawPolygonTool', 'Activated')
  }

  deactivate(): void {
    this.cancelDrawing()
    if (getToolMode() === 'pointer') {
      getViewer()?.getScene()?.getSelectionManager()?.setEnabled(true)
    }
    debug.log('DrawPolygonTool', 'Deactivated')
  }

  onMouseDown(ctx: ToolContext): void {
    if (ctx.event.button !== 0) return
    if (!ctx.worldPoint || !ctx.editorPoint) return

    // Check if closing the polygon (click near first vertex)
    if (this.vertices.length >= this.minPoints && this.firstVertexScreen) {
      const dx = ctx.event.clientX - this.firstVertexScreen.x
      const dy = ctx.event.clientY - this.firstVertexScreen.y
      if (dx * dx + dy * dy < CLOSE_THRESHOLD_PX * CLOSE_THRESHOLD_PX) {
        this.finishPolygon()
        ctx.event.preventDefault()
        return
      }
    }

    // Add vertex
    this.vertices.push({ x: ctx.editorPoint.x, y: ctx.editorPoint.y })

    // Store screen position of first vertex
    if (this.vertices.length === 1) {
      this.firstVertexScreen = { x: ctx.event.clientX, y: ctx.event.clientY }
    }

    this.updatePreview()
    ctx.event.preventDefault()
    ctx.event.stopPropagation()

    debug.log('DrawPolygonTool', `Added vertex ${this.vertices.length} at (${ctx.editorPoint.x}, ${ctx.editorPoint.y})`)
  }

  onMouseMove(ctx: ToolContext): void {
    if (!ctx.worldPoint) return
    this.currentWorld = ctx.worldPoint.clone()
    this.lastCamera = ctx.camera

    if (this.vertices.length > 0) {
      this.updatePreview()
    }

    // Show close cursor when near first vertex
    if (this.vertices.length >= this.minPoints && this.firstVertexScreen) {
      const dx = ctx.event.clientX - this.firstVertexScreen.x
      const dy = ctx.event.clientY - this.firstVertexScreen.y
      if (dx * dx + dy * dy < CLOSE_THRESHOLD_PX * CLOSE_THRESHOLD_PX) {
        this.toolManager.setCursor('pointer')
        return
      }
    }

    this.toolManager.setCursor(this.cursor)
  }

  onMouseUp(_ctx: ToolContext): void {
    // Click handling is in onMouseDown
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.cancelDrawing()
      this.toolManager.setActiveTool('select')
    } else if (event.key === 'Backspace') {
      if (this.vertices.length > 0) {
        this.vertices.pop()
        if (this.vertices.length === 0) {
          this.firstVertexScreen = null
        }
        this.updatePreview()
        debug.log('DrawPolygonTool', `Removed last vertex, ${this.vertices.length} remaining`)
      }
    } else if (event.key === 'Enter') {
      if (this.vertices.length >= this.minPoints) {
        this.finishPolygon()
      }
    }
  }

  dispose(): void {
    this.cancelDrawing()
    this.previewGroup.parent?.remove(this.previewGroup)
  }

  // --- Private ---

  private finishPolygon(): void {
    if (this.vertices.length < this.minPoints) return

    // Compute centroid
    let cx = 0, cy = 0
    for (const v of this.vertices) {
      cx += v.x
      cy += v.y
    }
    cx /= this.vertices.length
    cy /= this.vertices.length
    cx = Math.round(cx)
    cy = Math.round(cy)

    // Points relative to centroid
    const relativePoints = this.vertices.map(v => ({
      x: v.x - cx,
      y: v.y - cy,
    }))

    // Bounding box (will be auto-computed by ZoneState, but pass for callback)
    const xs = relativePoints.map(p => p.x)
    const ys = relativePoints.map(p => p.y)
    const width = Math.max(...xs) - Math.min(...xs)
    const height = Math.max(...ys) - Math.min(...ys)

    const id = this.config.onCreate({
      x: cx,
      y: cy,
      width,
      height,
      points: relativePoints,
    })

    debug.log('DrawPolygonTool', `Created polygon: ${id} with ${this.vertices.length} vertices`)

    this.cancelDrawing()
    this.toolManager.setActiveTool('select')
  }

  private cancelDrawing(): void {
    this.vertices = []
    this.currentWorld = null
    this.firstVertexScreen = null
    this.lastCamera = null
    this.clearPreview()
    this.config.overlay?.hideAll()
  }

  private updatePreview(): void {
    this.clearPreview()

    if (this.vertices.length === 0) return

    const color = this.config.previewColor ?? 0x4ecca3
    const y = 0.02

    // Build world-space points from editor coords
    const worldPoints: THREE.Vector3[] = this.vertices.map(v =>
      new THREE.Vector3(v.x / DPM, y, v.y / DPM)
    )

    // Add rubberband point (current mouse position)
    if (this.currentWorld) {
      worldPoints.push(new THREE.Vector3(this.currentWorld.x, y, this.currentWorld.z))
    }

    // Line preview (vertices + rubberband)
    if (worldPoints.length >= 2) {
      // Close the loop visually
      const linePoints = [...worldPoints, worldPoints[0]]
      const lineGeometry = new THREE.BufferGeometry().setFromPoints(linePoints)
      const lineMaterial = new THREE.LineBasicMaterial({
        color,
        transparent: true,
        opacity: 0.8,
      })
      this.previewLine = new THREE.Line(lineGeometry, lineMaterial)
      this.previewGroup.add(this.previewLine)
    }

    // Fill preview (only with 3+ points)
    if (worldPoints.length >= 3) {
      const shape = new THREE.Shape()
      // Use X/Z mapped to shape X/Y; negate Z so rotation to XZ plane matches line preview
      shape.moveTo(worldPoints[0].x, -worldPoints[0].z)
      for (let i = 1; i < worldPoints.length; i++) {
        shape.lineTo(worldPoints[i].x, -worldPoints[i].z)
      }
      shape.closePath()

      const fillGeometry = new THREE.ShapeGeometry(shape)
      const fillMaterial = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.2,
        side: THREE.DoubleSide,
        depthWrite: false,
      })

      this.previewFill = new THREE.Mesh(fillGeometry, fillMaterial)
      this.previewFill.rotation.x = -Math.PI / 2
      this.previewFill.position.y = y
      this.previewGroup.add(this.previewFill)
    }

    // Vertex dots
    const dotGeometry = new THREE.SphereGeometry(0.08, 8, 8)
    const dotMaterial = new THREE.MeshBasicMaterial({ color })
    for (const v of this.vertices) {
      const dot = new THREE.Mesh(dotGeometry, dotMaterial)
      dot.position.set(v.x / DPM, y + 0.05, v.y / DPM)
      this.previewGroup.add(dot)
    }

    // Highlight first vertex when closeable
    if (this.vertices.length >= this.minPoints) {
      const firstV = this.vertices[0]
      const highlightGeometry = new THREE.RingGeometry(0.1, 0.18, 16)
      const highlightMaterial = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.6,
        side: THREE.DoubleSide,
      })
      const ring = new THREE.Mesh(highlightGeometry, highlightMaterial)
      ring.rotation.x = -Math.PI / 2
      ring.position.set(firstV.x / DPM, y + 0.06, firstV.y / DPM)
      this.previewGroup.add(ring)
    }

    // Show live measurement overlays
    if (this.config.overlay && this.lastCamera && this.currentWorld) {
      const overlay = this.config.overlay
      const camera = this.lastCamera

      // Edge length: distance from last committed vertex to current mouse
      const lastVertex = this.vertices[this.vertices.length - 1]
      const lastWorldX = lastVertex.x / DPM
      const lastWorldZ = lastVertex.y / DPM
      const dx = this.currentWorld.x - lastWorldX
      const dz = this.currentWorld.z - lastWorldZ
      const edgeLen = Math.sqrt(dx * dx + dz * dz)
      const midX = (lastWorldX + this.currentWorld.x) / 2
      const midZ = (lastWorldZ + this.currentWorld.z) / 2
      overlay.showEdge(edgeLen, midX, midZ, camera)

      // Area: Shoelace on all committed vertices + current mouse (as dots)
      if (this.vertices.length >= 2) {
        const allPoints = [
          ...this.vertices,
          { x: this.currentWorld.x * DPM, y: this.currentWorld.z * DPM },
        ]
        const area = calculatePolygonAreaFromPoints(allPoints)
        // Show at centroid
        let cx = 0, cy = 0
        for (const p of allPoints) {
          cx += p.x / DPM
          cy += p.y / DPM
        }
        cx /= allPoints.length
        cy /= allPoints.length
        overlay.showArea(area, cx, cy, camera)
      } else {
        overlay.hideSlot('area')
      }

      // No dimensions slot for polygon
      overlay.hideSlot('dimensions')
    }
  }

  private clearPreview(): void {
    // Dispose all children
    while (this.previewGroup.children.length > 0) {
      const child = this.previewGroup.children[0]
      this.previewGroup.remove(child)
      if (child instanceof THREE.Mesh) {
        child.geometry?.dispose()
        if (child.material instanceof THREE.Material) {
          child.material.dispose()
        }
      } else if (child instanceof THREE.Line) {
        child.geometry?.dispose()
        if (child.material instanceof THREE.Material) {
          child.material.dispose()
        }
      }
    }
    this.previewFill = null
    this.previewLine = null
  }
}
